import { b2PrismaticJointDef, b2RevoluteJointDef, b2Vec2 } from "./box2d-compat.js";
import { FlashLevelBaseRuntime } from "./flash-level-base-runtime.js";

const BACKGROUND_SYMBOL = Object.freeze({
  imagePath: "port/generated/symbols/30/png/1.png",
  layout: {
    imageWidth: 5564.9,
    imageHeight: 2186.2,
    imageOffsetX: -1808.5,
    imageOffsetY: -616.65,
  },
  pngLayout: {
    sourceWidth: 5595,
    sourceHeight: 2217,
    cropX: 8,
    cropY: 8,
    cropWidth: 5578,
    cropHeight: 2201,
  },
});

const GOAL_SYMBOL = Object.freeze({
  imageWidth: 25.2,
  imageHeight: 46.5,
  imageOffsetX: -1.0,
  imageOffsetY: -15.2,
});

const CHECKPOINT_SYMBOL = Object.freeze({
  imageWidth: 10.8,
  imageHeight: 21.1,
  imageOffsetX: -0.5,
  imageOffsetY: -0.5,
});

const LEVEL4_SYMBOLS = Object.freeze({
  axePlatform: {
    imagePath: "port/generated/symbols/538/png/1.png",
    width: 920.7,
    height: 300.5,
    offsetX: -46.95,
    offsetY: -148.5,
  },
  secondFloor2: {
    imagePath: "port/generated/symbols/544/png/1.png",
    width: 584.0,
    height: 529.8,
    offsetX: -217.65,
    offsetY: -200.1,
  },
  secondFloor3: {
    imagePath: "port/generated/symbols/544/png/1.png",
    width: 584.0,
    height: 529.8,
    offsetX: -217.65,
    offsetY: -200.1,
  },
  step: {
    imagePath: "port/generated/symbols/552/png/1.png",
    width: 502.95,
    height: 300.5,
    offsetX: -146.15,
    offsetY: -112.55,
  },
  killBoom1: {
    imagePath: "port/generated/symbols/549/png/1.png",
    width: 261.0,
    height: 422.95,
    offsetX: -157.25,
    offsetY: -421.95,
  },
  killBoom2: {
    imagePath: "port/generated/symbols/549/png/1.png",
    width: 261.0,
    height: 422.95,
    offsetX: -157.25,
    offsetY: -421.95,
  },
  killBoom3: {
    imagePath: "port/generated/symbols/549/png/1.png",
    width: 261.0,
    height: 422.95,
    offsetX: -157.25,
    offsetY: -421.95,
  },
  axe1: {
    imagePath: "port/generated/symbols/554/png/1.png",
    width: 67.8,
    height: 176.35,
    offsetX: -0.5,
    offsetY: -0.5,
  },
  axe2: {
    imagePath: "port/generated/symbols/554/png/1.png",
    width: 67.8,
    height: 176.35,
    offsetX: -0.5,
    offsetY: -0.5,
  },
  dropPlatform: {
    imagePath: "port/generated/symbols/557/png/1.png",
    width: 502.95,
    height: 300.5,
    offsetX: -124.1,
    offsetY: -89.5,
  },
});

function drawSymbolImage(ctx, image, symbol, x, y, rotationDeg = 0) {
  if (!image?.complete || !symbol) {
    return;
  }
  ctx.save();
  ctx.translate(x, y);
  ctx.rotate((rotationDeg * Math.PI) / 180);
  ctx.drawImage(image, symbol.offsetX, symbol.offsetY, symbol.width, symbol.height);
  ctx.restore();
}

function drawAnimatedFrame(ctx, image, symbol, x, y) {
  if (!image?.complete || !symbol) {
    return;
  }
  ctx.save();
  ctx.translate(x, y);
  ctx.drawImage(image, symbol.imageOffsetX, symbol.imageOffsetY, symbol.imageWidth, symbol.imageHeight);
  ctx.restore();
}

const LEVEL4_BODY_LAYOUT_FIX = Object.freeze({
  mainPlatform: { x: -599.95, y: 440.0 },
  firstFloor: { x: -600.0, y: 260.0 },
  secondFloor1: { x: -355.0, y: 260.0 },
  secondFloor2: { x: -210.0, y: 260.0 },
  secondFloor3: { x: -65.0, y: 260.0 },
  axePlatform: { x: 508.05, y: 308.6 },
  dropPlatform: { x: 339.45, y: 308.5 },
});

export class LevelFourRuntime extends FlashLevelBaseRuntime {
  buildLevel() {
    if (this.assetsEnabled) {
      this.stageImage.src = "port/generated/levels_fg/level_4/1.visual.runtime.svg";
      this.backgroundImage = new Image();
      this.backgroundImage.src = BACKGROUND_SYMBOL.imagePath;
      this.backgroundStart = this.levelData.layout.namedChildren.backGroundMC;
      this.level4Images = Object.fromEntries(
        Object.entries(LEVEL4_SYMBOLS).map(([name, symbol]) => {
          const image = new Image();
          image.src = symbol.imagePath;
          return [name, image];
        }),
      );
    } else {
      this.level4Images = {};
    }

    // Level 4 generated layout currently has several static platform origins at (0,0).
    // Fixing these anchors restores correct collision/body placement without changing
    // any gameplay logic or per-frame physics behavior.
    for (const [name, fix] of Object.entries(LEVEL4_BODY_LAYOUT_FIX)) {
      const layout = this.levelData.layout.namedChildren[name];
      if (layout) {
        layout.x = fix.x;
        layout.y = fix.y;
      }
    }

    this.createBody("mainPlatform", "Polygon", 0, 1, 0.2, [[[810, 0], [810, 20], [0, 20], [0, 0]]]);
    this.createBody("firstFloor", "Polygon", 0, 1, 0.2, [[[200, 0], [200, 20], [0, 20], [0, 0]]]);
    this.createBody("secondFloor1", "Polygon", 0, 1, 0.2, [[[100, 0], [100, 20], [0, 20], [0, 0]]]);
    this.createBody("secondFloor2", "Polygon", 0, 1, 0.2, [[[100, 0], [100, 20], [0, 20], [0, 0]]]);
    this.createBody("secondFloor3", "Polygon", 0, 1, 0.2, [[[100, 0], [100, 20], [0, 20], [0, 0]]]);
    this.createBody("step", "Polygon", 0, 1, 0.2, [[[117, -10], [202, -10], [117, 12]], [[92, 12], [117, 12], [92, 34]], [[117, 12], [202, -10], [150, 16], [92, 34]], [[70, 34], [92, 34], [150, 122], [70, 57]], [[45, 57], [70, 57], [45, 76]], [[24, 76], [45, 76], [24, 99]], [[45, 76], [70, 57], [24, 99]], [[0, 99], [24, 99], [0, 122]], [[24, 99], [70, 57], [150, 122], [0, 122]], [[150, 122], [92, 34], [150, 16]], [[202, 16], [150, 16], [202, -10]]]);
    this.dropPlatformBody = this.createBody("dropPlatform", "Polygon", 0, 1, 0.2, [[[130, 0], [130, 10], [0, 10], [0, 0]]]);
    this.createBody("axePlatform", "Polygon", 0, 1, 0.2, [[[850, 0], [850, 20], [0, 20], [0, 0]]]);
    this.killBoom1Body = this.createBody("killBoom1", "Polygon", 1, 1, 0.2, [[[19.5, -267.5], [19.5, -31.5], [-19.5, -31.5], [-19.5, -267.5]], [[-19.5, -31.5], [19.5, -31.5], [50.5, 0.5], [-47.5, 0.5]]]);
    this.killBoom2Body = this.createBody("killBoom2", "Polygon", 1, 1, 0.2, [[[19.5, -267.5], [19.5, -31.5], [-19.5, -31.5], [-19.5, -267.5]], [[-19.5, -31.5], [19.5, -31.5], [50.5, 0.5], [-47.5, 0.5]]]);
    this.killBoom3Body = this.createBody("killBoom3", "Polygon", 1, 1, 0.2, [[[19.5, -267.5], [19.5, -31.5], [-19.5, -31.5], [-19.5, -267.5]], [[-19.5, -31.5], [19.5, -31.5], [50.5, 0.5], [-47.5, 0.5]]]);
    this.axe1Body = this.createBody("axe1", "Polygon", 2, 1, 0.2, [[[25.5, -1.5], [40.5, -1.5], [40.5, 113.5], [25.5, 113.5]], [[0.5, 116.5], [25.5, 113.5], [40.5, 113.5], [67.5, 116.5], [67.5, 175.5], [0.5, 175.5]]]);
    this.axe2Body = this.createBody("axe2", "Polygon", 2, 1, 0.2, [[[25.5, -1.5], [40.5, -1.5], [40.5, 113.5], [25.5, 113.5]], [[0.5, 116.5], [25.5, 113.5], [40.5, 113.5], [67.5, 116.5], [67.5, 175.5], [0.5, 175.5]]]);

    const prismatic = new b2PrismaticJointDef();
    const revolute = new b2RevoluteJointDef();
    prismatic.Initialize(this.killBoom1Body, this.ground, this.killBoom1Body.GetWorldCenter(), new b2Vec2(0, 1));
    prismatic.enableLimit = false;
    prismatic.enableMotor = false;
    this.world.CreateJoint(prismatic);
    this.killBoom1Up = true;

    prismatic.Initialize(this.killBoom2Body, this.ground, this.killBoom2Body.GetWorldCenter(), new b2Vec2(0, 1));
    prismatic.enableLimit = false;
    prismatic.enableMotor = false;
    this.world.CreateJoint(prismatic);
    this.killBoom1Up = true;

    prismatic.Initialize(this.killBoom3Body, this.ground, this.killBoom3Body.GetWorldCenter(), new b2Vec2(0, 1));
    prismatic.enableLimit = false;
    prismatic.enableMotor = false;
    this.world.CreateJoint(prismatic);
    this.killBoom1Up = true;

    revolute.Initialize(this.axe1Body, this.ground, new b2Vec2(785 / 30, 127 / 30));
    this.world.CreateJoint(revolute);
    revolute.Initialize(this.axe2Body, this.ground, new b2Vec2(1077 / 30, 127 / 30));
    this.world.CreateJoint(revolute);
  }

  render(ctx, stageWidth, stageHeight, options = {}) {
    const zoom = Math.min(4, Math.max(0.1, Number(options.zoom) || 1));
    ctx.save();
    ctx.clearRect(0, 0, stageWidth, stageHeight);
    ctx.translate(stageWidth / 2, stageHeight / 2);
    ctx.scale(zoom, zoom);
    ctx.translate(-stageWidth / 2, -stageHeight / 2);

    if (this.backgroundImage?.complete && this.backgroundStart) {
      const bx = this.backgroundStart.x - this.camera.x / 10;
      const by = this.backgroundStart.y - this.camera.y / 10;
      const sym = BACKGROUND_SYMBOL;
      ctx.save();
      ctx.translate(bx, by);
      const scaleX = sym.layout.imageWidth / sym.pngLayout.sourceWidth;
      const scaleY = sym.layout.imageHeight / sym.pngLayout.sourceHeight;
      ctx.drawImage(
        this.backgroundImage,
        sym.pngLayout.cropX, sym.pngLayout.cropY,
        sym.pngLayout.cropWidth, sym.pngLayout.cropHeight,
        sym.layout.imageOffsetX + sym.pngLayout.cropX * scaleX,
        sym.layout.imageOffsetY + sym.pngLayout.cropY * scaleY,
        sym.pngLayout.cropWidth * scaleX,
        sym.pngLayout.cropHeight * scaleY,
      );
      ctx.restore();
    }

    ctx.translate(this.camera.x, this.camera.y);

    if (this.stageImage?.complete) {
      const { layout } = this.levelData;
      ctx.drawImage(this.stageImage, layout.imageOffsetX, layout.imageOffsetY, layout.imageWidth, layout.imageHeight);
    }

    drawSymbolImage(
      ctx,
      this.level4Images.killBoom1,
      LEVEL4_SYMBOLS.killBoom1,
      this.getDisplayLayout("killBoom1").x,
      this.getDisplayLayout("killBoom1").y,
      this.getDisplayLayout("killBoom1").rotation ?? 0,
    );
    drawSymbolImage(
      ctx,
      this.level4Images.killBoom2,
      LEVEL4_SYMBOLS.killBoom2,
      this.getDisplayLayout("killBoom2").x,
      this.getDisplayLayout("killBoom2").y,
      this.getDisplayLayout("killBoom2").rotation ?? 0,
    );

    drawSymbolImage(
      ctx,
      this.level4Images.secondFloor2,
      LEVEL4_SYMBOLS.secondFloor2,
      this.levelData.layout.namedChildren.secondFloor2.x,
      this.levelData.layout.namedChildren.secondFloor2.y,
      this.levelData.layout.namedChildren.secondFloor2.rotation ?? 0,
    );

    drawSymbolImage(
      ctx,
      this.level4Images.killBoom3,
      LEVEL4_SYMBOLS.killBoom3,
      this.getDisplayLayout("killBoom3").x,
      this.getDisplayLayout("killBoom3").y,
      this.getDisplayLayout("killBoom3").rotation ?? 0,
    );

    for (const name of ["secondFloor3", "step", "axePlatform"]) {
      const layout = this.levelData.layout.namedChildren[name];
      drawSymbolImage(
        ctx,
        this.level4Images[name],
        LEVEL4_SYMBOLS[name],
        layout.x,
        layout.y,
        layout.rotation ?? 0,
      );
    }

    for (const name of ["axe1", "axe2"]) {
      const layout = this.getDisplayLayout(name);
      drawSymbolImage(
        ctx,
        this.level4Images[name],
        LEVEL4_SYMBOLS[name],
        layout.x,
        layout.y,
        layout.rotation ?? 0,
      );
    }

    const goalFrame = this.goalFrames[this.goalCurrentFrame - 1];
    drawAnimatedFrame(ctx, goalFrame, GOAL_SYMBOL, this.goalPosition.x, this.goalPosition.y);

    const dropLayout = this.getDisplayLayout("dropPlatform");
    drawSymbolImage(
      ctx,
      this.level4Images.dropPlatform,
      LEVEL4_SYMBOLS.dropPlatform,
      dropLayout.x,
      dropLayout.y,
      dropLayout.rotation ?? 0,
    );

    for (const checkpointState of this.checkpoints) {
      const checkpointFrame = this.checkpointFrames[checkpointState.currentFrame - 1];
      drawAnimatedFrame(ctx, checkpointFrame, CHECKPOINT_SYMBOL, checkpointState.x, checkpointState.y);
    }

    if (this.player?.isLive?.()) {
      ctx.save();
      ctx.translate(this.player.x, this.player.y);
      ctx.rotate(this.player.body.GetAngle());
      if (this.playerImage?.complete) {
        ctx.drawImage(this.playerImage, -10.5, -10.5);
      }
      ctx.restore();
    }

    ctx.fillStyle = "#d32f2f";
    for (const particle of this.particles) {
      ctx.beginPath();
      ctx.arc(particle.x, particle.y, 4, 0, Math.PI * 2);
      ctx.fill();
    }

    ctx.restore();
  }

  levelUpdate() {
    if (
      this.player.y > 650
      || this.contactListener.playerContactBodies.indexOf(this.killBoom1Body) !== -1
      || this.contactListener.playerContactBodies.indexOf(this.killBoom2Body) !== -1
      || this.contactListener.playerContactBodies.indexOf(this.killBoom3Body) !== -1
      || this.contactListener.playerContactBodies.indexOf(this.axe1Body) !== -1
      || this.contactListener.playerContactBodies.indexOf(this.axe2Body) !== -1
    ) {
      if (this.player.isLive()) {
        this.startDeath();
      }
    }
    if (this.contactListener.playerContactBodies.indexOf(this.dropPlatformBody) !== -1) {
      this.destroyBody(this.dropPlatformBody);
      this.dropPlatformBody = this.createBody("dropPlatform", "Polygon", 3, 1, 0.2, [[[130, 0], [130, 10], [0, 10], [0, 0]]]);
      this.refreshNamedRects();
    }
    const boomChecks = [
      ["killBoom1", this.killBoom1Body, "killBoom1Up"],
      ["killBoom2", this.killBoom2Body, "killBoom2Up"],
      ["killBoom3", this.killBoom3Body, "killBoom3Up"],
    ];
    for (const [name, body, stateName] of boomChecks) {
      const layout = this.getDisplayLayout(name);
      if (layout.y > 436) {
        this[stateName] = true;
      }
      if (layout.y < 327) {
        this[stateName] = false;
      }
      if (this[stateName]) {
        body.SetLinearVelocity(new b2Vec2(0, -2));
      }
    }
  }
}
